/**
 * 事件埋点系统 - 统一入口
 * 支持4种埋点类型：
 * 1. APK上架包埋点
 * 2. Facebook API埋点
 * 3. AppsFlyer API埋点
 * 4. Adjust API埋点
 */

import { env } from './env'
import pinia, { useUserStore, useAppStore } from '@/stores'

// 声明jsBridge类型
declare global {
  interface Window {
    jsBridge?: {
      postMessage: (eventName: string, params?: string) => void
    }
  }
}

// 声明 AppsFlyer 全局类型
declare global {
  interface Window {
    jsBridge?: {
      postMessage: (eventName: string, params?: string) => void
    }
    AppsFlyerSdkObject?: string
    AF?: {
      q?: any[]
      id?: string
      plugins?: Record<string, any>
      initSdk?: (config: any) => void
      logEvent?: (eventName: string, eventValues?: Record<string, any>) => void
      setCustomerUserId?: (userId: string) => void
    } | (() => void)
  }
}

// 埋点类型枚举
export enum TrackingType {
  APK = 'apk',
  FACEBOOK = 'facebook',
  APPSFLYER = 'appsflyer',
  ADJUST = 'adjust'
}

// 基础事件接口
export interface BaseTrackingEvent {
  eventName: string
  timestamp: number
  uid?: string | number
  phone?: string
  email?: string
  cid?: string
  domain?: string
}

// APK埋点事件接口 - 简化为统一入口
export interface APKTrackingEvent extends BaseTrackingEvent {
  params?: Record<string, any>
}


// AppsFlyer埋点事件接口
export interface AppsFlyerTrackingEvent extends BaseTrackingEvent {
  eventName: string
  eventValues?: Record<string, any>
  eventCurrency?: string
  eventRevenue?: number
}

// Adjust埋点事件接口
export interface AdjustTrackingEvent {
  eventToken: string
  revenue?: number
  currency?: string
  callbackParams?: Array<{ key: string; value: string }>
  partnerParams?: Array<{ key: string; value: string }>
  deduplicationId?: string
}

// 埋点配置接口
export interface TrackingConfig {
  enabled: boolean
  debug: boolean
  batchSize: number
  flushInterval: number
  endpoint?: string
}

// 默认配置
const defaultConfig: TrackingConfig = {
  enabled: true,
  debug: env.isDev,
  batchSize: 10,
  flushInterval: 5000,
  endpoint: env.apiUrl
}

// APK埋点类
export class APKTracking {
  private config: TrackingConfig
  private uid?: string | number
  private phone?: string
  private email?: string
  private cid?: string
  private domain?: string

  constructor(config: Partial<TrackingConfig> = {}) {
    this.config = { ...defaultConfig, ...config }
    if (typeof window !== 'undefined' && window.location?.origin) {
      // 默认从环境推断域名
      this.domain = window.location.origin.endsWith('/')
        ? window.location.origin
        : `${window.location.origin}/`
    }

    // 初始化渠道 cid
    this.cid = this.resolveCidFromEnv()

    // 从用户 store 初始化并订阅更新
    this.tryInitFromUserStore()
  }

  // 创建基础事件对象
  private createBaseEvent(eventName: string): APKTrackingEvent {
    return {
      eventName,
      timestamp: Date.now(),
      uid: this.uid,
      phone: this.phone,
      email: this.email,
      cid: this.cid,
      domain: this.domain,
    }
  }

  // 从 Pinia 的 user store 初始化/更新用户上下文
  private tryInitFromUserStore() {
    try {
      const userStore = useUserStore(pinia)
      this.applyUserStore(userStore)
      // 订阅后续变更（detached 确保无需组件上下文）
      userStore.$subscribe(() => {
        this.applyUserStore(userStore)
      }, { detached: true })
    } catch (error) {
      if (this.config.debug) {
        console.warn('[APK Tracking] Failed to init from user store:', error)
      }
    }
  }

  private applyUserStore(userStore: ReturnType<typeof useUserStore>) {
    const maybeRef: any = (userStore as any).userInfo
    const user = maybeRef?.value ?? maybeRef
    if (user && typeof user === 'object') {
      // userId 优先，其次 id
      this.uid = user.userId ?? user.id ?? this.uid
      // 可能由 info() 返回的数据包含以下字段
      if (user.phone !== undefined) this.phone = user.phone
      if (user.email !== undefined) this.email = user.email
    }
  }

  private resolveCidFromEnv(): string | undefined {
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href)
        const fromQuery = url.searchParams.get('cid') || undefined
        const fromStorage = window.localStorage?.getItem('cid') || undefined
        return fromQuery || fromStorage || this.cid
      } catch {
        // ignore
      }
    }
    return this.cid
  }


  // 发送事件到jsBridge
  private sendToJsBridge(eventName: string, params?: Record<string, any>) {
    if (typeof window !== 'undefined' && window.jsBridge?.postMessage) {
      try {
        window.jsBridge.postMessage(eventName, params ? JSON.stringify(params) : JSON.stringify({}))
        if (this.config.debug) {
          console.log('[APK Tracking] Sent to jsBridge:', { eventName, params })
        }
      } catch (error) {
        console.error('[APK Tracking] Failed to send to jsBridge:', error)
      }
    } else {
      console.warn('[APK Tracking] jsBridge not available')
    }
  }

  // 统一事件发送入口
  public trackEvent(eventName: string, params?: Record<string, any>) {
    if (!this.config.enabled) return
    const base = this.createBaseEvent(eventName)
    const mergedParams = { ...base, ...(params || {}) }
    if (this.config.debug) {
      console.log('[APK Tracking] Event:', eventName, mergedParams)
    }
    this.sendToJsBridge(eventName, mergedParams)
  }

  // 登录事件
  public trackLogin(params?: Record<string, any>) {
    this.trackEvent('login', params)
  }

  // 登出事件
  public trackLogout(params?: Record<string, any>) {
    this.trackEvent('logout', params)
  }

  // 点击注册事件
  public trackRegisterClick(params?: Record<string, any>) {
    this.trackEvent('registerclick', params)
  }

  // 注册成功事件
  public trackRegister(params?: Record<string, any>) {
    this.trackEvent('register', params)
  }

  // 点击充值事件
  public trackRechargeClick(params?: Record<string, any>) {
    this.trackEvent('rechargeclick', params)
  }

  // 首充成功事件
  public trackFirstRecharge(params?: Record<string, any>) {
    this.trackEvent('firstrecharge', params)
  }

  // 复充成功事件
  public trackRecharge(params?: Record<string, any>) {
    this.trackEvent('recharge', params)
  }

  // 进入游戏事件
  public trackEnterGame(params?: Record<string, any>) {
    this.trackEvent('entergame', params)
  }

  // 设置用户与渠道上下文
  public setUid(uid: string | number) {
    this.uid = uid
  }

  // 兼容旧方法名
  public setUserId(userId: string | number) {
    this.setUid(userId)
  }

  public setPhone(phone: string) {
    this.phone = phone
  }

  public setEmail(email: string) {
    this.email = email
  }

  public setCid(cid: string) {
    this.cid = cid
  }

  public setDomain(domain: string) {
    this.domain = domain
  }

  public setUserContext(context: {
    uid?: string | number
    phone?: string
    email?: string
    cid?: string
    domain?: string
  }) {
    if (context.uid !== undefined) this.uid = context.uid
    if (context.phone !== undefined) this.phone = context.phone
    if (context.email !== undefined) this.email = context.email
    if (context.cid !== undefined) this.cid = context.cid
    if (context.domain !== undefined) this.domain = context.domain
  }
}

// Facebook埋点类（占位符）
export class FacebookTracking {
  constructor(config: Partial<TrackingConfig> = {}) {
    // TODO: 实现Facebook埋点
  }
}

// AppsFlyer埋点类
export class AppsFlyerTracking {
  private appsFlyerInstance: any
  private isInitialized = false
  private devKey?: string

  constructor() {
    // 构造函数不进行初始化，等待外部调用 init 方法
  }

  // 动态加载 AppsFlyer SDK
  private async loadAppsFlyerSDK(): Promise<void> {
    function loadScript(t, e, n, s, a, c, i, o, p) {
      t.AppsFlyerSdkObject = a, t.AF = t.AF || function () {
        (t.AF.q = t.AF.q || []).push([Date.now()].concat(Array.prototype.slice.call(arguments)))
      },
        t.AF.id = t.AF.id || i, t.AF.plugins = {}, o = e.createElement(n), p = e.getElementsByTagName(n)[0], o.async = 1,
        o.src = "https://websdk.appsflyer.com?" + (c.length > 0 ? "st=" + c.split(",").sort().join(",") + "&" : "") + (i.length > 0 ? "af_id=" + i : ""),
        p.parentNode.insertBefore(o, p)
    }
    return new Promise((resolve, reject) => {
      // 动态插入 AppsFlyer SDK 脚本
      loadScript(window, document, "script", 0, "AF", "pba", { pba: { webAppId: this.devKey || '' } }, document.head, document.body)
      resolve()
    })
  }
  // private async loadAppsFlyerSDK(): Promise<void> {
  //   return new Promise((resolve, reject) => {
  //     if (typeof window === 'undefined') {
  //       reject(new Error('Window is not defined'))
  //       return
  //     }

  //     // 检查是否已经加载
  //     if ((window as any).AF) {
  //       resolve()
  //       return
  //     }

  //     // 严格按照 AppsFlyer 官方代码实现
  //     const t = window
  //     const e = document
  //     const n = 'script'
  //     const s = 0
  //     const a = 'AF'
  //     const c = 'pba' // st 参数
  //     const i = this.devKey || '' // af_id 参数，使用 devKey
  //     const o = e.createElement(n)
  //     const p = e.getElementsByTagName(n)[0]

  //     // 设置 AppsFlyer SDK 对象
  //     t.AppsFlyerSdkObject = a
  //     t.AF = t.AF || function () {
  //       (t.AF as any).q = (t.AF as any).q || []
  //         ; (t.AF as any).q.push([Date.now()].concat(Array.prototype.slice.call(arguments)))
  //     }
  //       ; (t.AF as any).id = (t.AF as any).id || i
  //       ; (t.AF as any).plugins = {}

  //     // 设置脚本属性
  //     o.async = true
  //     o.src = "https://websdk.appsflyer.com?" + (c.length > 0 ? "st=" + c.split(",").sort().join(",") + "&" : "") + (i.length > 0 ? "af_id=" + i : "")

  //     // 插入脚本
  //     p.parentNode?.insertBefore(o, p)

  //     // 监听脚本加载完成
  //     o.onload = () => resolve()
  //     o.onerror = () => reject(new Error('Failed to load AppsFlyer SDK'))
  //   })
  // }

  // 初始化 AppsFlyer SDK
  public async init(devKey: string, debug: boolean = false) {
    if (typeof window === 'undefined' || this.isInitialized) return

    try {
      // 设置 devKey 用于 SDK 加载
      this.devKey = devKey

      // 检查是否已加载 AppsFlyer SDK
      if (typeof window !== 'undefined' && (window as any).AF) {
        this.appsFlyerInstance = (window as any).AF
      } else {
        // 动态加载 AppsFlyer SDK
        await this.loadAppsFlyerSDK()
        this.appsFlyerInstance = (window as any).AF
      }

      this.isInitialized = true

      if (debug) {
        console.log('[AppsFlyer] SDK initialized successfully with devKey:', devKey)
      }
    } catch (error) {
      console.error('[AppsFlyer] Failed to initialize SDK:', error)
    }
  }

  // 静态方法：初始化 AppsFlyer 并返回实例
  public static async initAppsFlyer(devKey: string, debug: boolean = false): Promise<AppsFlyerTracking> {
    if (!devKey) {
      console.warn('[AppsFlyer] Dev key is required')
      throw new Error('AppsFlyer dev key is required')
    }

    try {
      const appsFlyerTracking = new AppsFlyerTracking()
      await appsFlyerTracking.init(devKey, debug)
      return appsFlyerTracking
    } catch (error) {
      console.error('[AppsFlyer] Failed to initialize:', error)
      throw error
    }
  }

  // 统一事件发送入口
  public trackEvent(eventName: string, eventValues?: Record<string, any>) {
    if (!this.isInitialized || !this.appsFlyerInstance) {
      console.warn('[AppsFlyer] SDK not initialized')
      return
    }

    try {
      if (import.meta.env.VITE_DEV === 'true') {
        console.log('[AppsFlyer] Event:', { eventName, eventValues })
      }
      // 使用正确的 AppsFlyer 事件发送格式
      window.AF?.('pba', 'event', {
        eventType: 'EVENT',
        eventValue: eventValues || {},
        eventName: eventName
      })
    } catch (error) {
      console.error('[AppsFlyer] Failed to track event:', error)
    }
  }

  // 登录事件
  public trackLogin(eventValues?: Record<string, any>) {
    this.trackEvent('Login', eventValues)
  }

  // 登出事件
  public trackLogout(eventValues?: Record<string, any>) {
    this.trackEvent('Logout', eventValues)
  }

  // 注册成功事件
  public trackRegister(eventValues?: Record<string, any>) {
    this.trackEvent('CompleteRegistration', eventValues)
  }

  // 首充成功事件
  public trackFirstRecharge(revenue: number, currency: string = 'INR', eventValues?: Record<string, any>) {
    this.trackEvent('Purchase', {
      eventRevenueCurrency: currency,
      eventRevenue: revenue,
      ...eventValues
    })
  }

  // 复充成功事件
  public trackRecharge(revenue: number, currency: string = 'INR', eventValues?: Record<string, any>) {
    this.trackEvent('Recharge', {
      eventRevenueCurrency: currency,
      eventRevenue: revenue,
      ...eventValues
    })
  }
  // 点击充值事件
  public trackRechargeClick(revenue: number, currency: string = 'INR', eventValues?: Record<string, any>) {
    this.trackEvent('RechargeClick', {
      eventRevenueCurrency: currency,
      eventRevenue: revenue,
      ...eventValues
    })
  }

  // 进入游戏事件
  public trackEnterGame(eventValues?: Record<string, any>) {
    this.trackEvent('EnterGame', eventValues)
  }
  // 点击注册事件
  public trackRegisterClick(eventValues?: Record<string, any>) {
    this.trackEvent('RegisterClick', eventValues)
  }

  // 落地页展示
  public trackLandingPageView(eventValues?: Record<string, any>) {
    this.trackEvent('PageView', eventValues)
  }
  // 落地页安装点击
  public trackLandingPageInstall(eventValues?: Record<string, any>) {
    this.trackEvent('Install', eventValues)
  }

  // 设置用户ID
  public setCustomerUserId(userId: string) {
    if (!this.isInitialized || !this.appsFlyerInstance) {
      console.warn('[AppsFlyer] SDK not initialized')
      return
    }

    try {
      this.appsFlyerInstance.setCustomerUserId(userId)
    } catch (error) {
      console.error('[AppsFlyer] Failed to set customer user ID:', error)
    }
  }

  // 获取 AppsFlyer 实例（用于高级操作）
  public getAppsFlyerInstance() {
    return this.appsFlyerInstance
  }

  // 检查是否已初始化
  public isSDKInitialized() {
    return this.isInitialized
  }

  // 获取当前 Dev Key
  public getDevKey() {
    return this.devKey
  }
}

// Adjust埋点类
export class AdjustTracking {
  private adjustInstance: any
  private isInitialized = false
  private appToken?: string
  public webUuid?: string
  constructor() {
    // 构造函数不进行初始化，等待外部调用 init 方法
  }

  // 从 store 获取 adjustTrackingConfig
  private getAdjustTrackingConfig() {
    try {
      const appStore = useAppStore(pinia)
      return appStore.adjustTrackingConfig
    } catch (error) {
      console.warn('[Adjust] Failed to get adjustTrackingConfig from store:', error)
      return null
    }
  }

  // 初始化 Adjust SDK
  public async init(appToken: string, debug: boolean = false) {
    if (typeof window === 'undefined' || this.isInitialized) return

    try {
      // 动态导入 Adjust SDK
      const adjust = await import('@adjustcom/adjust-web-sdk')

      // Adjust 配置
      const adjustConfig = {
        appToken,
        environment: (debug ? 'sandbox' : 'production') as 'sandbox' | 'production',
        logLevel: (debug ? 'verbose' : 'info') as 'verbose' | 'info',
        // 自定义回调
        attributionCallback: (e: any, attribution: any) => {
          if (debug) {
            console.log('[Adjust] Attribution callback:', attribution)
          }
          // 可以在这里处理归因数据
        }
      }

      // 初始化 Adjust
      adjust.default.initSdk(adjustConfig)
      this.adjustInstance = adjust.default
      this.appToken = appToken
      this.isInitialized = true
      adjust.default.waitForWebUUID().then((webUuid: string) => {
        console.log('[Adjust] Web UUID:', webUuid)
        this.webUuid = webUuid
      })
      if (debug) {
        console.log('[Adjust] SDK initialized successfully with appToken:', appToken)
      }
    } catch (error) {
      console.error('[Adjust] Failed to initialize SDK:', error)
    }
  }

  // 静态方法：初始化 Adjust 并返回实例
  public static async initAdjust(appToken: string, debug: boolean = false): Promise<AdjustTracking> {
    if (!appToken) {
      console.warn('[Adjust] No app token available')
      throw new Error('Adjust app token is required')
    }

    try {
      const adjustTracking = new AdjustTracking()
      await adjustTracking.init(appToken, debug)
      return adjustTracking
    } catch (error) {
      console.error('[Adjust] Failed to initialize:', error)
      throw error
    }
  }

  // 统一事件发送入口
  public trackEvent(params: AdjustTrackingEvent) {
    if (!this.isInitialized || !this.adjustInstance) {
      console.warn('[Adjust] SDK not initialized')
      return
    }

    try {
      if (import.meta.env.VITE_DEV === 'true') {
        console.log('[Adjust] Event:', params)
      }

      // 发送事件
      this.adjustInstance.trackEvent(params)
    } catch (error) {
      console.error('[Adjust] Failed to track event:', error)
    }
  }

  // 登录事件
  public trackLogin(callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.login
    if (!eventToken) {
      console.warn('[Adjust] Login eventToken not found in adjustTrackingConfig')
      return
    }
    this.trackEvent({
      eventToken,
      callbackParams
    })
  }

  // 登出事件
  public trackLogout(callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.logout
    if (!eventToken) {
      console.warn('[Adjust] Logout eventToken not found in adjustTrackingConfig')
      return
    }
    this.trackEvent({
      eventToken,
      callbackParams
    })
  }

  // 注册成功事件
  public trackRegister(callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.completeRegistration
    if (!eventToken) {
      console.warn('[Adjust] Register eventToken not found in adjustTrackingConfig')
      return
    }
    this.trackEvent({
      eventToken,
      callbackParams
    })
  }

  // 首充成功事件
  public trackFirstRecharge(revenue: number, currency: string = 'INR', callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.purchase
    if (!eventToken) {
      console.warn('[Adjust] FirstRecharge eventToken not found in adjustTrackingConfig')
      return
    }
    this.trackEvent({
      eventToken,
      revenue,
      currency,
      callbackParams
    })
  }

  // 复充成功事件
  public trackRecharge(revenue: number, currency: string = 'INR', callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.recharge
    if (!eventToken) {
      console.warn('[Adjust] Recharge eventToken not found in adjustTrackingConfig')
      return
    }
    this.trackEvent({
      eventToken,
      revenue,
      currency,
      callbackParams
    })
  }

  // 进入游戏事件
  public trackEnterGame(callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.enterGame
    if (!eventToken) {
      console.warn('[Adjust] EnterGame eventToken not found in adjustTrackingConfig')
      return
    }
    this.trackEvent({
      eventToken,
      callbackParams
    })
  }

  // 注册点击事件
  public trackRegisterClick(callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.registerClick
    if (!eventToken) {
      console.warn('[Adjust] RegisterClick eventToken not found in adjustTrackingConfig')
      return
    }
    this.trackEvent({
      eventToken,
      callbackParams
    })
  }

  // 充值点击事件
  public trackRechargeClick(revenue: number, currency: string = 'INR', callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.rechargeClick
    if (!eventToken) {
      console.warn('[Adjust] RechargeClick eventToken not found in adjustTrackingConfig')
      return
    }
    this.trackEvent({
      eventToken,
      revenue,
      currency,
      callbackParams
    })
  }

  // 落地页展示
  public trackLandingPageView(callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.pageView
    if (!eventToken) {
      console.warn('[Adjust] LandingPageView eventToken not found in adjustTrackingConfig')
      return
    }
    this.trackEvent({
      eventToken,
      callbackParams
    })
  }
  // 落地页安装点击
  public trackLandingPageInstall(callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.install
    if (!eventToken) {
      console.warn('[Adjust] LandingPageInstall eventToken not found in adjustTrackingConfig')
      return
    }
    this.trackEvent({
      eventToken,
      callbackParams
    })
  }

  // 首次进入网站
  public trackFirstOpen(callbackParams?: Array<{ key: string; value: string }>) {
    const config = this.getAdjustTrackingConfig()
    const eventToken = config?.firstOpen
    if (!eventToken) {
      console.warn('[Adjust] FirstOpen eventToken not found in adjustTrackingConfig')
      return
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      const storageKey = 'adjust_first_open_tracked';
      const hasTracked = window.localStorage.getItem(storageKey);

      if (!hasTracked) {
        // 记录首次打开事件
        this.trackEvent({
          eventToken,
          callbackParams
        })

        // 标记为已记录
        window.localStorage.setItem(storageKey, 'true');

        console.log('Adjust: 首次打开事件已记录');
        return true;
      } else {
        console.log('Adjust: 首次打开事件已记录过，跳过');
        return false;
      }
    }
    this.trackEvent({
      eventToken,
      callbackParams
    })
  }

  // 获取 Adjust 实例（用于高级操作）
  public getAdjustInstance() {
    return this.adjustInstance
  }

  // 检查是否已初始化
  public isSDKInitialized() {
    return this.isInitialized
  }

  // 获取当前 App Token
  public getAppToken() {
    return this.appToken
  }
}
